import { useState } from 'react';
import { BookPlus, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { useCreateItem } from '@/hooks/pricebook/useCreateItem';
import { useHasAnyPermission } from '@/hooks/usePermissions';
import {
  buildPricebookItemFromLine,
  isDuplicateCodeError,
  promotionBlockedReason,
  type PromotableLine,
} from '@/lib/sales/promote-line-to-pricebook';

interface SaveLineToPricebookButtonProps {
  line: PromotableLine;
  /** Called with the created item so the line can point at it. */
  onSaved: (item: { id: string; code: string; itemVersion?: number }) => void;
}

/** How many times a colliding code is retried with a suffix before giving up. */
const MAX_CODE_ATTEMPTS = 5;

/**
 * Keep a hand-typed line: create the catalog item it describes, and point the
 * line at it.
 *
 * The line already carries everything the item needs — price, cost, tax class,
 * accounts, member pricing, labor — so the operator who built it on one estimate
 * does not have to build it again on the next. Until now that work was thrown
 * away the moment the estimate was saved.
 */
export function SaveLineToPricebookButton({ line, onSaved }: SaveLineToPricebookButtonProps) {
  const canCreate = useHasAnyPermission('company:create', 'pricing:create');
  const createItem = useCreateItem();
  const [savedCode, setSavedCode] = useState<string | null>(null);

  // Nothing to offer someone who cannot write to the catalog: the action would
  // only ever end in a 403 they can do nothing about.
  if (!canCreate) return null;

  if (savedCode) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-success">
        <Check className="size-3.5" aria-hidden="true" />
        Saved to pricebook as {savedCode}
      </span>
    );
  }

  const blocked = promotionBlockedReason(line);

  const save = async () => {
    if (blocked) {
      toast.error(blocked);
      return;
    }

    // A tenant's codes are unique and the code is derived from the name, so two
    // lines called "Service call" collide. Retry with a suffix rather than
    // handing the operator a constraint violation to solve.
    for (let attempt = 0; attempt < MAX_CODE_ATTEMPTS; attempt += 1) {
      const dto = buildPricebookItemFromLine(line, { codeAttempt: attempt });
      try {
        const created = (await createItem.mutateAsync(dto)) as {
          id?: string;
          code?: string;
          itemVersion?: number;
          data?: { id?: string; code?: string; itemVersion?: number };
        };
        const item = created?.data ?? created;
        if (!item?.id) {
          toast.error('The pricebook item was created but did not come back with an id.');
          return;
        }
        onSaved({ id: item.id, code: item.code ?? dto.code, itemVersion: item.itemVersion });
        setSavedCode(item.code ?? dto.code);
        return;
      } catch (error) {
        if (isDuplicateCodeError(error) && attempt < MAX_CODE_ATTEMPTS - 1) continue;
        // useCreateItem's mutation already surfaces the server's message; this
        // only covers the case where every suffix was taken.
        if (isDuplicateCodeError(error)) {
          toast.error(`A pricebook item already uses ${dto.code}. Rename the line and try again.`);
        }
        return;
      }
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 gap-1.5 text-xs"
      disabled={createItem.isPending || !!blocked}
      title={blocked ?? 'Create a pricebook item from this line and link it'}
      onClick={() => void save()}
    >
      {createItem.isPending ? (
        <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
      ) : (
        <BookPlus className="size-3.5" aria-hidden="true" />
      )}
      Save to pricebook
    </Button>
  );
}
